import { useState, useEffect, useCallback, useRef } from 'react'
import {
  useMicrophonePermission,
  PermissionState,
} from '@/features/yakureki/hooks/useMicrophonePermission'
import { useRecordingState } from '@/features/yakureki/hooks/useRecordingState'
import { convertToMp3 } from '@/features/yakureki/apis/audio'

const MAX_DURATION_SECONDS = 120

interface UseVoiceMemoReturn {
  isRecording: boolean
  audioURL: string | null
  audioBlob: Blob | null
  isConverting: boolean
  recordingDuration: number
  permissionStatus: PermissionState
  startRecording: () => Promise<void>
  stopRecording: () => void
  cancelRecording: () => void
  clearAudio: () => void
}

/**
 * 音声メモ録音フック（SoapUpdateChat用）
 *
 * 既存の useMicrophonePermission + useRecordingState を組み合わせ、
 * 録音タイマー・自動停止・MP3変換を追加。
 */
export const useVoiceMemo = (): UseVoiceMemoReturn => {
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // マイク権限（autoRequest: false — ダイアログ開いただけでは権限要求しない）
  const { permissionStatus, mediaRecorder, requestMicrophoneAccess } =
    useMicrophonePermission(true, { autoRequest: false })

  // 録音状態管理
  const {
    isRecording,
    audioURL,
    startRecording: baseStartRecording,
    stopRecording: baseStopRecording,
    cancelRecording: baseCancelRecording,
    clearAudio: baseClearAudio,
  } = useRecordingState(
    mediaRecorder,
    permissionStatus,
    requestMicrophoneAccess
  )

  // 録音中タイマー
  useEffect(() => {
    if (isRecording) {
      setRecordingDuration(0)
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1)
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isRecording])

  // 120秒で自動停止
  useEffect(() => {
    if (isRecording && recordingDuration >= MAX_DURATION_SECONDS) {
      baseStopRecording()
    }
  }, [isRecording, recordingDuration, baseStopRecording])

  // 録音完了時にMP3変換
  useEffect(() => {
    if (audioURL && !audioBlob && !isConverting) {
      const convert = async () => {
        setIsConverting(true)
        try {
          const response = await fetch(audioURL)
          const rawBlob = await response.blob()
          const mp3Blob = await convertToMp3(rawBlob)
          setAudioBlob(mp3Blob)
        } catch {
          // 変換失敗時はrawのまま使用
          const response = await fetch(audioURL)
          setAudioBlob(await response.blob())
        } finally {
          setIsConverting(false)
        }
      }
      convert()
    }
  }, [audioURL, audioBlob, isConverting])

  const clearAudio = useCallback(() => {
    baseClearAudio()
    setAudioBlob(null)
    setRecordingDuration(0)
  }, [baseClearAudio])

  const cancelRecording = useCallback(() => {
    baseCancelRecording()
    setAudioBlob(null)
    setRecordingDuration(0)
  }, [baseCancelRecording])

  return {
    isRecording,
    audioURL,
    audioBlob,
    isConverting,
    recordingDuration,
    permissionStatus,
    startRecording: baseStartRecording,
    stopRecording: baseStopRecording,
    cancelRecording,
    clearAudio,
  }
}
